import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const AuthContext = createContext(null);
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARN_BEFORE_MS = 60 * 1000;       // warn 1 minute before logout

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, object=logged
  const [error, setError] = useState("");
  const idleTimerRef = useRef(null);
  const warnTimerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const clearIdleTimers = () => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
  };

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setError(typeof msg === "string" ? msg : "Login failed");
      throw e;
    }
  };

  const logout = useCallback(async (reason) => {
    clearIdleTimers();
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    if (reason === "idle") {
      toast.warning("You have been logged out after 30 minutes of inactivity.");
    }
  }, []);

  // -------- 30-minute idle auto-logout --------
  useEffect(() => {
    if (!user || user === false) {
      clearIdleTimers();
      return;
    }
    const resetTimers = () => {
      clearIdleTimers();
      warnTimerRef.current = setTimeout(() => {
        toast.warning("You will be logged out in 1 minute due to inactivity.", { duration: 8000 });
      }, IDLE_TIMEOUT_MS - WARN_BEFORE_MS);
      idleTimerRef.current = setTimeout(() => {
        logout("idle");
      }, IDLE_TIMEOUT_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
    let lastReset = 0;
    const onActivity = () => {
      const now = Date.now();
      // throttle resets to once every 5s to avoid timer churn
      if (now - lastReset < 5000) return;
      lastReset = now;
      resetTimers();
    };
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    resetTimers();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearIdleTimers();
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, refresh, error, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
